import { For, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { orgHref } from '../lib/org';
import type { RepoRow } from '../lib/repos';

// The `invalid` step's content. Presenter.
//
// "That .maintainer.yml didn't validate. Fix it and try again." named nothing a
// maintainer could act on — not the repo, not the field, not the rule — and
// Try again only returned to the spinner. `/v1/repos` already carries
// `policy_valid` per repo (#980, the same field the Repos grid's badge reads),
// so the REPO is nameable from data this client holds, and the repo's Policy tab
// already renders the resolved snapshot with the refusal list.
//
// What is still missing is the API's own reason: `GET /v1/onboarding/state`
// serves `{account_id, state, next_move, complete}` and carries no validation
// `detail`, so the specific field and line cannot be shown here. Naming the repo
// and pointing at the surface that DOES hold the detail is the whole of what
// this client can honestly say today.
export type OnboardingInvalidNoticeProps = {
  readonly repos: readonly RepoRow[] | undefined;
};

export function OnboardingInvalidNotice(props: OnboardingInvalidNoticeProps): JSX.Element {
  const offenders = (): readonly RepoRow[] =>
    (props.repos ?? []).filter((r) => r.policy_status === 'invalid');
  return (
    <>
      <div class="alert alert--destructive" role="alert">
        <p class="alert__title">{t('onboarding.validate_invalid')}</p>
        <p class="alert__desc">{t('onboarding.validate_invalid_body')}</p>
      </div>
      <Show when={offenders().length > 0}>
        <ul class="onboarding__repos">
          <For each={offenders()}>
            {(repo) => (
              <li>
                <a href={orgHref(`/repos/${encodeURIComponent(repo.full_name)}/policy`)}>
                  {t('onboarding.validate_invalid_repo', { repo: repo.full_name })}
                </a>
              </li>
            )}
          </For>
        </ul>
      </Show>
    </>
  );
}
